import React, { useState, useEffect } from 'react';
import { PhotoValidator } from './PhotoValidator';
import { ContentValidator } from './ContentValidator';
import { ProfileSetupData, PhotoValidationResult, ContentValidationResult } from '../types';
import {
  PreviewContainer,
  PreviewHeader,
  PreviewTitle,
  PreviewDescription,
  PreviewContent,
  ProfileCard,
  ProfileHeader,
  ProfilePhoto,
  ProfileInfo,
  ProfileName,
  ProfileAge,
  ProfileLocation,
  ProfileBio,
  ProfileInterests,
  InterestTag,
  ProfilePreferences,
  PreferenceItem,
  ValidationPanel,
  ValidationTabs,
  ValidationTab,
  ValidationContent,
  ValidationStatus,
  StatusItem,
  StatusIcon,
  StatusMessage,
  PreviewActions,
  ActionButton,
  EditButton,
  PublishButton,
  ShareButton,
  PreviewStats,
  StatItem,
  StatValue,
  StatLabel
} from '../../../styles/Profile/ProfilePreview.styles';

interface ProfilePreviewProps {
  profileData: ProfileSetupData;
  onEdit: () => void;
  onPublish: () => void;
  onShare: () => void;
}

export const ProfilePreview: React.FC<ProfilePreviewProps> = ({
  profileData,
  onEdit,
  onPublish,
  onShare
}) => {
  const [activeValidationTab, setActiveValidationTab] = useState('photos');
  const [validationResults, setValidationResults] = useState({
    photos: { photoId: 'overall', isValid: true, errors: [], warnings: [], issues: [] } as PhotoValidationResult,
    content: { field: 'bio' as const, isValid: true, errors: [], warnings: [], suggestions: [], score: 100 } as ContentValidationResult,
    overall: { isValid: true, score: 0 }
  });

  const calculateAge = (dateOfBirth: Date): number => {
    const today = new Date();
    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
      age--;
    }
    
    return age;
  };

  const getValidationStatus = () => {
    const photoValidation = validationResults.photos.isValid;
    const contentValidation = validationResults.content.isValid;
    const overallValidation = validationResults.overall.isValid;
    
    return {
      photoValidation,
      contentValidation,
      overallValidation,
      totalErrors: validationResults.photos.errors.length + validationResults.content.errors.length,
      totalWarnings: validationResults.photos.warnings.length + validationResults.content.warnings.length
    };
  };

  const getProfileStats = () => {
    const totalPhotos = profileData.photos.length;
    const validatedPhotos = profileData.photos.filter(photo => photo.isValidated).length;
    const totalInterests = profileData.interests.length;
    const bioLength = profileData.bio.content.length;
    const completionPercentage = Math.round(
      ((totalPhotos > 0 ? 1 : 0) + 
       (bioLength > 50 ? 1 : 0) + 
       (totalInterests >= 3 ? 1 : 0) + 
       (profileData.basicInfo.firstName ? 1 : 0) + 
       (profileData.basicInfo.location.city ? 1 : 0)) * 20
    );
    
    return {
      totalPhotos,
      validatedPhotos,
      totalInterests,
      bioLength,
      completionPercentage
    };
  };

  const stats = getProfileStats();
  const validationStatus = getValidationStatus();

  const renderProfileCard = () => {
    // An image, never the video. Both can be main now, so a type-blind
    // find() would put a video file where a photo belongs.
    const profileImages = profileData.photos.filter(photo => photo.type !== 'video');
    const mainPhoto = profileImages.find(photo => photo.isMain) || profileImages[0];
    const age = calculateAge(profileData.basicInfo.dateOfBirth);
    
    return (
      <ProfileCard>
        <ProfileHeader>
          <ProfilePhoto>
            {mainPhoto ? (
              <img src={mainPhoto.url} alt="Profile" />
            ) : (
              <div className="placeholder">📷</div>
            )}
          </ProfilePhoto>
          
          <ProfileInfo>
            <ProfileName>
              {profileData.basicInfo.firstName} {profileData.basicInfo.lastName}
            </ProfileName>
            <ProfileAge>{age} years old</ProfileAge>
            <ProfileLocation>
              {profileData.basicInfo.location.city}, {profileData.basicInfo.location.state}
            </ProfileLocation>
          </ProfileInfo>
        </ProfileHeader>
        
        <ProfileBio>
          <h4>About Me</h4>
          <p>{profileData.bio.content || 'No bio added yet'}</p>
        </ProfileBio>
        
        <ProfileInterests>
          <h4>Interests</h4>
          <div className="interests-grid">
            {profileData.interests.map(interest => (
              <InterestTag key={interest.id}>
                <span className="icon">{interest.icon}</span>
                <span className="name">{interest.name}</span>
              </InterestTag>
            ))}
          </div>
        </ProfileInterests>
        
        <ProfilePreferences>
          <h4>Looking For</h4>
          <PreferenceItem>
            <strong>Age:</strong> {profileData.preferences.ageRange.min}-{profileData.preferences.ageRange.max} years
          </PreferenceItem>
          <PreferenceItem>
            <strong>Distance:</strong> Within {profileData.preferences.distance} miles
          </PreferenceItem>
          <PreferenceItem>
            <strong>Gender:</strong> {profileData.preferences.genderPreference.join(', ')}
          </PreferenceItem>
          <PreferenceItem>
            <strong>Relationship:</strong> {profileData.preferences.interestedIn.join(', ')}
          </PreferenceItem>
        </ProfilePreferences>
      </ProfileCard>
    );
  };

  const renderValidationPanel = () => {
    return (
      <ValidationPanel>
        <ValidationTabs>
          <ValidationTab
            $isActive={activeValidationTab === 'photos'}
            onClick={() => setActiveValidationTab('photos')}
          >
            Photos
          </ValidationTab>
          <ValidationTab
            $isActive={activeValidationTab === 'content'}
            onClick={() => setActiveValidationTab('content')}
          >
            Content
          </ValidationTab>
        </ValidationTabs>
        
        <ValidationContent>
          {activeValidationTab === 'photos' && (
            <PhotoValidator
              photos={profileData.photos}
              onValidationChange={(results) => 
                setValidationResults(prev => ({ ...prev, photos: results }))
              }
            />
          )}
          
          {activeValidationTab === 'content' && (
            <ContentValidator
              bio={profileData.bio}
              interests={profileData.interests}
              basicInfo={profileData.basicInfo}
              onValidationChange={(results) => 
                setValidationResults(prev => ({ ...prev, content: results }))
              }
            />
          )}
        </ValidationContent>
      </ValidationPanel>
    );
  };

  return (
    <PreviewContainer>
      <PreviewHeader>
        <PreviewTitle>Profile Preview</PreviewTitle>
        <PreviewDescription>
          Review your profile before publishing. Make sure everything looks good and meets our guidelines.
        </PreviewDescription>
      </PreviewHeader>

      <PreviewStats>
        <StatItem>
          <StatValue>{stats.completionPercentage}%</StatValue>
          <StatLabel>Complete</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.totalPhotos}</StatValue>
          <StatLabel>Photos</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.validatedPhotos}</StatValue>
          <StatLabel>Validated</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.totalInterests}</StatValue>
          <StatLabel>Interests</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.bioLength}</StatValue>
          <StatLabel>Bio Length</StatLabel>
        </StatItem>
      </PreviewStats>

      <PreviewContent>
        <div className="preview-section">
          <h3>How Others Will See You</h3>
          {renderProfileCard()}
        </div>
        
        <div className="validation-section">
          <h3>Profile Validation</h3>
          {renderValidationPanel()}
        </div>
      </PreviewContent>

      <ValidationStatus>
        <StatusItem $isValid={validationStatus.photoValidation}>
          <StatusIcon>
            {validationStatus.photoValidation ? '✓' : '✗'}
          </StatusIcon>
          <StatusMessage>
            Photos: {validationStatus.photoValidation ? 'Valid' : 'Issues found'}
          </StatusMessage>
        </StatusItem>
        
        <StatusItem $isValid={validationStatus.contentValidation}>
          <StatusIcon>
            {validationStatus.contentValidation ? '✓' : '✗'}
          </StatusIcon>
          <StatusMessage>
            Content: {validationStatus.contentValidation ? 'Valid' : 'Issues found'}
          </StatusMessage>
        </StatusItem>
        
        <StatusItem $isValid={validationStatus.overallValidation}>
          <StatusIcon>
            {validationStatus.overallValidation ? '✓' : '✗'}
          </StatusIcon>
          <StatusMessage>
            Overall: {validationStatus.overallValidation ? 'Ready to publish' : 'Needs attention'}
          </StatusMessage>
        </StatusItem>
      </ValidationStatus>

      <PreviewActions>
        <EditButton onClick={onEdit}>
          Edit Profile
        </EditButton>
        
        <ShareButton onClick={onShare}>
          Share Preview
        </ShareButton>
        
        <PublishButton
          onClick={onPublish}
          disabled={!validationStatus.overallValidation}
        >
          Publish Profile
        </PublishButton>
      </PreviewActions>
    </PreviewContainer>
  );
};
